/**
 * Dev Console 数据工厂面板 · W2-9 PR-2
 *
 * 承接：CLAUDE.md §11.5.1 数据工厂（admin /dev-console 子页 · 一次建后续复用）
 *
 * 4 个 panel：
 * 1. 造 session（POST /admin/v1/dev/seed/sessions）
 * 2. 造 trace（POST /admin/v1/dev/seed/traces）
 * 3. YAML 场景跑器（POST /admin/v1/dev/scenarios/run）
 * 4. 清理（DELETE /admin/v1/dev/seed/cleanup）
 *
 * 注：本面板请求**真打后端**（不走 mock 拦截器），目的是验证投产 PG 路径。
 * 前端 ?mock=1 拦截器只在 fixture 注册中心命中时短路，dev API 不注册 → 透传。
 */
import { useState } from "react";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select } from "@/components/ui/select";
import { useCleanup, useRunScenario, useSeedSessions, useSeedTraces } from "@/lib/api/dev";

const DEFAULT_YAML = `name: 客户怒火三连击
steps:
  - action: seed_session
    params:
      intent: refund
      progression_state: closing
  - action: seed_trace
    params:
      final_outcome: failed
      total_latency_ms: 1200
`;

export function DataFactoryPanel() {
	const [sessionCount, setSessionCount] = useState(3);
	const [sessionIntent, setSessionIntent] = useState("refund");
	const [traceCount, setTraceCount] = useState(1);
	const [traceOutcome, setTraceOutcome] = useState<"success" | "partial" | "failed" | "handoff">(
		"failed",
	);
	const [scenarioName, setScenarioName] = useState("anger_test");
	const [yamlText, setYamlText] = useState(DEFAULT_YAML);

	const seedSessions = useSeedSessions();
	const seedTraces = useSeedTraces();
	const runScenario = useRunScenario();
	const cleanup = useCleanup();

	return (
		<div className="space-y-4">
			<Card data-testid="data-factory-sessions">
				<CardHeader>
					<CardTitle>造 Session（POST /admin/v1/dev/seed/sessions）</CardTitle>
					<CardDescription>
						默认写入 mer_devcons 哨兵商户。首次调用自动 IDEMPOTENT 创建哨兵。
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-3">
					<div className="flex flex-wrap items-end gap-3">
						<label className="text-sm">
							数量
							<input
								type="number"
								min={1}
								max={50}
								value={sessionCount}
								onChange={(e) => setSessionCount(Number(e.target.value))}
								className="ml-2 w-20 rounded border border-surface-border px-2 py-1"
								data-testid="data-factory-sessions-count"
							/>
						</label>
						<span className="text-sm">
							Intent
							<Select
								className="ml-2"
								aria-label="Intent"
								value={sessionIntent}
								onChange={(e) => setSessionIntent(e.target.value)}
							>
								<option value="know">know</option>
								<option value="refund">refund</option>
								<option value="complaint">complaint</option>
								<option value="outreach">outreach</option>
							</Select>
						</span>
						<button
							type="button"
							className="rounded-md border border-surface-border bg-surface-card px-3 py-1.5 text-sm hover:bg-surface-subtle disabled:opacity-50"
							disabled={seedSessions.isPending}
							data-testid="data-factory-sessions-submit"
							onClick={() => seedSessions.mutate({ count: sessionCount, intent: sessionIntent })}
						>
							{seedSessions.isPending ? "造中..." : "投产"}
						</button>
					</div>
					{seedSessions.data ? (
						<div
							className="rounded-md bg-surface-subtle p-2 text-xs"
							data-testid="data-factory-sessions-result"
						>
							✅ 造了 {seedSessions.data.created.length} 条 session
							{seedSessions.data.bootstrap_merchant_inserted ? " · 哨兵商户已自举" : ""}
						</div>
					) : null}
					{seedSessions.error ? (
						<div className="rounded-md bg-brand-danger/10 p-2 text-xs text-brand-danger">
							❌ {seedSessions.error.message}
						</div>
					) : null}
				</CardContent>
			</Card>

			<Card data-testid="data-factory-traces">
				<CardHeader>
					<CardTitle>造 Trace（POST /admin/v1/dev/seed/traces）</CardTitle>
					<CardDescription>
						无 session_id 时自动取 mer_devcons 最新 1 条 session 关联（都没有则自建）。
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-3">
					<div className="flex flex-wrap items-end gap-3">
						<label className="text-sm">
							数量
							<input
								type="number"
								min={1}
								max={50}
								value={traceCount}
								onChange={(e) => setTraceCount(Number(e.target.value))}
								className="ml-2 w-20 rounded border border-surface-border px-2 py-1"
							/>
						</label>
						<span className="text-sm">
							结果
							<Select
								className="ml-2"
								aria-label="结果"
								value={traceOutcome}
								onChange={(e) =>
									setTraceOutcome(e.target.value as "success" | "partial" | "failed" | "handoff")
								}
							>
								<option value="success">success</option>
								<option value="partial">partial</option>
								<option value="failed">failed</option>
								<option value="handoff">handoff</option>
							</Select>
						</span>
						<button
							type="button"
							className="rounded-md border border-surface-border bg-surface-card px-3 py-1.5 text-sm hover:bg-surface-subtle disabled:opacity-50"
							disabled={seedTraces.isPending}
							onClick={() => seedTraces.mutate({ count: traceCount, final_outcome: traceOutcome })}
						>
							{seedTraces.isPending ? "造中..." : "投产"}
						</button>
					</div>
					{seedTraces.data ? (
						<div className="rounded-md bg-surface-subtle p-2 text-xs">
							✅ 造了 {seedTraces.data.created.length} 条 trace
							{seedTraces.data.auto_created_session_id
								? ` · 自建 session ${seedTraces.data.auto_created_session_id.slice(0, 12)}...`
								: ""}
						</div>
					) : null}
					{seedTraces.error ? (
						<div className="rounded-md bg-brand-danger/10 p-2 text-xs text-brand-danger">
							❌ {seedTraces.error.message}
						</div>
					) : null}
				</CardContent>
			</Card>

			<Card data-testid="data-factory-scenarios">
				<CardHeader>
					<CardTitle>YAML 场景跑器（POST /admin/v1/dev/scenarios/run）</CardTitle>
					<CardDescription>
						支持 seed_session / seed_trace 两类 step（PR-2 v0）。step 间隐式串联。
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-3">
					<input
						type="text"
						value={scenarioName}
						onChange={(e) => setScenarioName(e.target.value)}
						placeholder="场景名称"
						className="w-full rounded border border-surface-border px-2 py-1 text-sm"
					/>
					<textarea
						value={yamlText}
						onChange={(e) => setYamlText(e.target.value)}
						className="h-48 w-full rounded border border-surface-border p-2 font-mono text-xs"
						data-testid="data-factory-scenarios-yaml"
					/>
					<button
						type="button"
						className="rounded-md border border-surface-border bg-surface-card px-3 py-1.5 text-sm hover:bg-surface-subtle disabled:opacity-50"
						disabled={runScenario.isPending}
						data-testid="data-factory-scenarios-run"
						onClick={() => runScenario.mutate({ name: scenarioName, yaml_text: yamlText })}
					>
						{runScenario.isPending ? "运行中..." : "运行场景"}
					</button>
					{runScenario.data ? (
						<div className="rounded-md bg-surface-subtle p-2 text-xs">
							✅ {runScenario.data.name} · {runScenario.data.succeeded}/
							{runScenario.data.total_steps} 步成功
						</div>
					) : null}
					{runScenario.error ? (
						<div className="rounded-md bg-brand-danger/10 p-2 text-xs text-brand-danger">
							❌ {runScenario.error.message}
						</div>
					) : null}
				</CardContent>
			</Card>

			<Card data-testid="data-factory-cleanup">
				<CardHeader>
					<CardTitle>清理（DELETE /admin/v1/dev/seed/cleanup）</CardTitle>
					<CardDescription>
						默认仅清 mer_devcons 哨兵商户下数据（不动真实商户 · 防误删）。
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-3">
					<button
						type="button"
						className="rounded-md border border-surface-border bg-surface-card px-3 py-1.5 text-sm hover:bg-surface-subtle disabled:opacity-50"
						disabled={cleanup.isPending}
						data-testid="data-factory-cleanup-submit"
						onClick={() => cleanup.mutate(undefined)}
					>
						{cleanup.isPending ? "清理中..." : "清理 mer_devcons 数据"}
					</button>
					{cleanup.data ? (
						<div className="rounded-md bg-surface-subtle p-2 text-xs">
							✅ 删 {cleanup.data.sessions_deleted} session · {cleanup.data.traces_deleted} trace
						</div>
					) : null}
					{cleanup.error ? (
						<div className="rounded-md bg-brand-danger/10 p-2 text-xs text-brand-danger">
							❌ {cleanup.error.message}
						</div>
					) : null}
				</CardContent>
			</Card>
		</div>
	);
}
